import { useEffect } from 'react';

import withErrorBoundary from '@skyscanner-internal/landing-pages-shared-ui/src/components/ErrorBoundary';
import ErrorBoundary from '@skyscanner-internal/landing-pages-shared-ui/src/components/ErrorBoundary/ErrorBoundary';
import IntersectionObserverWrapper from '@skyscanner-internal/landing-pages-shared-ui/src/components/IntersectionObserverWrapper/IntersectionObserverWrapper';
import { MBDContextProvider } from '@skyscanner-internal/landing-pages-shared-ui/src/components/common/MBD/useMBDContext';
import withEmotionCache from '@skyscanner-internal/landing-pages-shared-ui/src/hooks/withEmotionCache';
import { logExperimentChokepoint } from '@skyscanner-internal/landing-pages-shared-ui/src/logMiniEvent';
import logAdvertReceived from '@skyscanner-internal/landing-pages-shared-ui/src/utils/logAdvertReceived';
import classnames from 'classnames';

import componentMap from '../../components';

import logGlobalComponentVisible from './logGlobalComponentVisible';

import type { FeatureContainerProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FeatureContainerProps';

import STYLES from './FeatureContainer.module.scss';

const FeatureContainer = (props: FeatureContainerProps): JSX.Element => {
  const {
    advert,
    components,
    experimentChokepoints,
    fullWidthLayout = false,
    hasStrevda,
    headerComponents,
  } = props;

  useEffect(() => {
    experimentChokepoints?.forEach((chokepoint) => {
      logExperimentChokepoint(chokepoint);
    });
  }, [experimentChokepoints]);

  const applyFullWidthLayout = !hasStrevda && fullWidthLayout;

  if (advert) {
    logAdvertReceived(advert);
  }

  let content = (
    <>
      {components.map(
        ({
          componentConfigName,
          componentProps,
          contentId,
          dataIdConfigSource,
          enableVisibleMiniEvent,
          id,
          isCriticalComponent,
          name,
          pageType,
          rolloutStatus,
          vertical,
        }) => {
          const PageComponent = componentMap[name];
          const componentIdentifier = id || name;
          if (!PageComponent) return null;
          return (
            <ErrorBoundary
              key={componentIdentifier}
              componentName={`feature-container-component.${name}`}
              metadata={{ isCriticalComponent }}
            >
              <div
                id={componentIdentifier}
                data-content-id={contentId}
                data-id-config-source={
                  dataIdConfigSource || 'static--not-platform-component'
                }
                data-id-rollout-status={
                  dataIdConfigSource === 'deferred--content-api-ready'
                    ? rolloutStatus
                    : undefined
                }
                className={classnames(STYLES.FeatureItem, {
                  [STYLES.FeatureItemConstrained]:
                    applyFullWidthLayout && componentProps?.fullWidth !== true,
                })}
              >
                <MBDContextProvider
                  rawComponentName={componentConfigName}
                  vertical={vertical}
                  pageType={pageType}
                >
                  {enableVisibleMiniEvent ? (
                    <IntersectionObserverWrapper
                      onElementSeen={logGlobalComponentVisible(
                        componentConfigName,
                      )}
                    >
                      <PageComponent {...componentProps} />
                    </IntersectionObserverWrapper>
                  ) : (
                    <PageComponent {...componentProps} />
                  )}
                </MBDContextProvider>
              </div>
              <div id={`sp-slot-after-${componentIdentifier}`} />
            </ErrorBoundary>
          );
        },
      )}
    </>
  );

  if (advert && componentMap[advert.name]) {
    const PageComponent = componentMap[advert.name];
    const componentIdentifier = advert.id || advert.name;

    content = (
      <ErrorBoundary
        key={componentIdentifier}
        componentName={`feature-container-component.${advert.name}`}
      >
        <div id={componentIdentifier} className={STYLES.FeatureItem}>
          <PageComponent {...advert.componentProps}>{content}</PageComponent>
        </div>
        <div id={`sp-slot-after-${componentIdentifier}`} />
      </ErrorBoundary>
    );
  }

  const containerContent = (
    <div className={STYLES.FeatureContainer}>
      {headerComponents && (
        <div>
          {headerComponents.map(
            ({
              componentConfigName,
              componentProps,
              contentId,
              dataIdConfigSource,
              enableVisibleMiniEvent,
              id,
              isCriticalComponent,
              name,
              pageType,
              rolloutStatus,
              vertical,
            }) => {
              const PageComponent = componentMap[name];
              const componentIdentifier = id || name;
              if (!PageComponent) return null;
              return (
                <ErrorBoundary
                  key={componentIdentifier}
                  componentName={`feature-container-component.${name}`}
                  metadata={{ isCriticalComponent }}
                >
                  <div
                    id={componentIdentifier}
                    data-content-id={contentId}
                    data-id-config-source={
                      dataIdConfigSource || 'static--not-platform-component'
                    }
                    data-id-rollout-status={
                      dataIdConfigSource === 'deferred--content-api-ready'
                        ? rolloutStatus
                        : undefined
                    }
                    className={STYLES.HeaderItem}
                  >
                    <MBDContextProvider
                      rawComponentName={componentConfigName}
                      vertical={vertical}
                      pageType={pageType}
                    >
                      {enableVisibleMiniEvent ? (
                        <IntersectionObserverWrapper
                          onElementSeen={logGlobalComponentVisible(
                            componentConfigName,
                          )}
                        >
                          <PageComponent {...componentProps} />
                        </IntersectionObserverWrapper>
                      ) : (
                        <PageComponent {...componentProps} />
                      )}
                    </MBDContextProvider>
                  </div>
                </ErrorBoundary>
              );
            },
          )}
        </div>
      )}
      <div
        className={classnames(
          hasStrevda ? STYLES.MainSectionWithAD : STYLES.MainSection,
          {
            [STYLES.MainSectionFullWidth]: applyFullWidthLayout,
          },
        )}
      >
        <div className={STYLES.Main} id="feature-container-main">
          {content}
        </div>
        {hasStrevda ? (
          <aside id="strevda-mpu-wrapper" className={STYLES.Sidebar} />
        ) : null}
      </div>
    </div>
  );

  return containerContent;
};

export default withErrorBoundary(
  withEmotionCache(FeatureContainer),
  'feature-container-component',
);
